import compact from "lodash-es/compact";

import { usePricePlanContext } from "../../core/contexts/PricePlanContext/PricePlanContext";
import { usePricePlanFeatureContext } from "../../core/contexts/PricePlanFeatureContext/PricePlanFeatureContext";
import { GeneralLangFactory, SettingsLangFactory } from "../../core/lang";
import * as m from "../../core/model/model";
import { formatMessage } from "../../core/utils/formatMessage";
import { getPlanName } from "./getPlanName";
import { getPricePlanFeatureAddOnName } from "./getPricePlanFeatureAddOnName";

import BokioPlan = m.Entities.BokioPlan;
import PricePlanFeature = m.Entities.PricePlanFeature;
import InclusionType = m.Bokio.Billing.Contract.Models.Subscriptions.SubscriptionAddonInclusionType;

interface PricePlanFeatureUpgradeHookProps {
	feature: PricePlanFeature;
	hasUpgradePermission: boolean;
}

export interface PricePlanFeatureUpgradeHookBadge {
	/**
	 * Texts in markdown format.
	 */
	text: string;
	/**
	 * Texts in markdown format.
	 */
	tooltipRows: string[];
}

export type PricePlanFeatureUpgradeHookIcon = "add-on-feature" | "bolt";

export type PricePlanFeatureUpgradeActionType = "start-trial-addon" | "upgrade-plan";

interface PricePlanFeatureUpgradeHookHandle {
	icon: PricePlanFeatureUpgradeHookIcon;
	title: string;
	/**
	 * Texts in markdown format.
	 */
	shortDescription: string;
	/**
	 * Texts in markdown format including links connected to the web app.
	 * These links needs to be stripped away in the mobile app.
	 */
	descriptionTexts: string[];
	/**
	 * Undefined if there is not enough user permission to upgrade the plan.
	 */
	cta: string | undefined;
	badge: PricePlanFeatureUpgradeHookBadge;
	/**
	 * Indicates the type of action available to the user.
	 * - 'start-trial-addon': User can start addon trial (never tried before)
	 * - 'upgrade-plan': User needs to upgrade plan or purchase addon
	 */
	actionType: PricePlanFeatureUpgradeActionType;
}

const getPlanFeaturesText = (plan: BokioPlan) => {
	const settingsLang = SettingsLangFactory();

	switch (plan) {
		case BokioPlan.FreeShared: // This shouldn't happen bug we need to keep it for backwards compatibility
		case BokioPlan.Free:
		case BokioPlan.Beta:
		case BokioPlan.Balance:
		case BokioPlan.Business:
		case BokioPlan.Invalid:
		case BokioPlan.Trial_2025:
			return "";
		case BokioPlan.Premium:
			return settingsLang.Plans_UpgradeModal_Premium_Features;
		case BokioPlan.Pro:
			return settingsLang.Plans_UpgradeModal_Pro_Features;
		case BokioPlan.Basic_2025:
			return settingsLang.Plans_UpgradeModal_Basic_2025_Features;
		case BokioPlan.Premium_2025:
			return settingsLang.Plans_UpgradeModal_Premium_2025_Features;
		case BokioPlan.Business_2025:
			return settingsLang.Plans_UpgradeModal_Business_2025_Features;
		case BokioPlan.Plus_2025:
			return settingsLang.Plans_UpgradeModal_Plus_2025_Features;
	}
};

/**
 * If feature is available this will return undefined.
 * See test placed in n/.../usePricePlanFeatureUpgrade.spec.ts
 */

export const usePricePlanFeatureUpgrade = ({
	feature,
	hasUpgradePermission,
}: PricePlanFeatureUpgradeHookProps): PricePlanFeatureUpgradeHookHandle | undefined => {
	const generalLang = GeneralLangFactory();
	const settingsLang = SettingsLangFactory();
	const { inTrial, plan } = usePricePlanContext();

	const {
		getPlansOrAddonNeededForPricePlanFeature,
		isPricePlanFeatureAvailableInTrial,
		isPricePlanFeatureEnabled,
		getAddonForPricePlanFeature,
	} = usePricePlanFeatureContext();

	const plansOrAddonNeeded = getPlansOrAddonNeededForPricePlanFeature({
		feature,
		currentPlan: plan,
		companyInTrial: inTrial,
	});
	const lowestPlan = plansOrAddonNeeded.plansNeeded[0];
	const lowestPlanName = getPlanName(lowestPlan);

	const lockedDueToTrial = inTrial && !isPricePlanFeatureAvailableInTrial(feature);

	if (isPricePlanFeatureEnabled(feature)) {
		return undefined;
	}

	if (lockedDueToTrial) {
		// We don't support addons text here yet because when when this was added we didn't have any features
		// that weren't available in trial and also had an addon.
		const shortDescription = formatMessage(settingsLang.Plans_UpgradeModal_TrialPlanRequired, lowestPlanName);

		return {
			icon: "bolt",
			title: settingsLang.Plans_UpgradeModal_PaidPlanRequired,
			shortDescription: shortDescription,
			descriptionTexts: compact([
				shortDescription,
				settingsLang.Plans_UpgradeModal_TrialPlanRequiredUnlock,
				!hasUpgradePermission && generalLang.BokioPlanLockBadge_ToolTip_NoPermission,
			]),
			badge: {
				text: formatMessage(generalLang.BokioPlanLockBadge_Text_RequiresPlanX, lowestPlanName),
				tooltipRows: compact([
					shortDescription,
					!hasUpgradePermission && generalLang.BokioPlanLockBadge_ToolTip_NoPermission,
				]),
			},
			cta: hasUpgradePermission ? generalLang.UpgradePlan_CTA : undefined,
			actionType: "upgrade-plan",
		};
	}

	const canAccessFeatureByActivatingAddon = plansOrAddonNeeded.addonNeeded;
	const addonTrialDays = canAccessFeatureByActivatingAddon?.TrialDays;
	const addon = getAddonForPricePlanFeature(feature);

	// Determine if user can start addon trial
	// - Addon must be available for this feature on current plan
	// - TrialDays must be configured (not null/undefined)
	// - User has never started trial or purchased addon
	const canStartAddonTrial =
		canAccessFeatureByActivatingAddon != null && // (1) Addon available for this feature
		addonTrialDays != null && // (2) TrialDays configured
		addonTrialDays > 0 && // (3) TrialDays > 0
		addon?.InclusionType === InclusionType.CanBePurchased && // (4) Not yet purchased
		addon?.TrialEndDate == null; // (5) Never started trial

	if (canStartAddonTrial) {
		const addonName = getPricePlanFeatureAddOnName(feature);
		const shortDescription = formatMessage(
			generalLang.BokioPlanLockPromotionBanner_Addon_StartTrial,
			addonName,
			addonTrialDays,
		);
		const addonTrialCta = hasUpgradePermission
			? formatMessage(generalLang.BokioPlanLockPromotionBanner_Addon_StartTrial_CTA, addonTrialDays)
			: undefined;

		return {
			icon: "add-on-feature",
			title: formatMessage(generalLang.BokioPlanLockPromotionBanner_Addon_StartTrial_Title, addonName, addonTrialDays),
			shortDescription: shortDescription,
			descriptionTexts: compact([
				shortDescription,
				!hasUpgradePermission && generalLang.BokioPlanLockBadge_ToolTip_NoPermission,
			]),
			badge: {
				text: generalLang.BokioPlanLockBadge_Text_RequiresAddon,
				tooltipRows: compact([
					shortDescription,
					!hasUpgradePermission && generalLang.BokioPlanLockBadge_ToolTip_NoPermission,
				]),
			},
			cta: addonTrialCta,
			actionType: "start-trial-addon",
		};
	}

	// We don't support if you are on a price plan and that the add-on is available on another price plan. E.g. You are on Basic
	// but the Tags add-on is only available on Premium. We build this but we don't have it yet.
	const shortDescription = canAccessFeatureByActivatingAddon
		? formatMessage(
				settingsLang.Plans_RequiresPlanXOrAddon_Locked,
				getPricePlanFeatureAddOnName(feature),
				lowestPlanName,
			)
		: formatMessage(settingsLang.Plans_RequiresPlanX_Locked, lowestPlanName);

	return {
		icon: canAccessFeatureByActivatingAddon ? "add-on-feature" : "bolt",
		title: formatMessage(generalLang.UpgradeToX, lowestPlanName),
		shortDescription: shortDescription,
		descriptionTexts: compact([
			shortDescription,
			getPlanFeaturesText(lowestPlan),
			settingsLang.Plans_UpgradeModal_MoreAlternatives,
			!hasUpgradePermission && generalLang.BokioPlanLockBadge_ToolTip_NoPermission,
		]),
		badge: {
			text: canAccessFeatureByActivatingAddon
				? generalLang.BokioPlanLockBadge_Text_RequiresAddon
				: formatMessage(generalLang.BokioPlanLockBadge_Text_RequiresPlanX, lowestPlanName),
			tooltipRows: compact([
				shortDescription,
				!hasUpgradePermission && generalLang.BokioPlanLockBadge_ToolTip_NoPermission,
			]),
		},
		cta: hasUpgradePermission
			? canAccessFeatureByActivatingAddon
				? generalLang.Activate
				: generalLang.UpgradePlan_CTA
			: undefined,
		actionType: "upgrade-plan",
	};
};
